import { useMemo, useEffect, useCallback, useState } from 'react';
import { Button, ConfigProvider, Empty, Input } from 'antd';
import Form from 'components/Form';
import Card from 'components/Card/Card';
import Space, { Spacer } from 'components/Space/Space';
import { Close, Search } from '@carbon/icons-react';
import PageMeta from 'components/PageMeta/PageMeta';
import PageTitleBar from 'components/PageTitleBar/PageTitleBar';
import FamilyDashboardTable from './FamilyDashboardTable';
import { commonService } from 'services';
import { useUrlParams } from 'hooks/useUrlParams';
import { BigNumber } from 'bignumber.js';
import FamilyDashboardFilters from './FamilyDashboardFilters';

const PAGE_SIZE = 10;

const INITIAL_SORTER_INFO = {
  columnKey: 'caseId',
  order: 'desc',
};

function processFiltersQueryParam(_filters) {
  return {
    ..._filters,
    branchIds:
      typeof _filters?.branchIds === 'string'
        ? _filters?.branchIds?.split?.(',')?.map((d) => Number(d))
        : _filters?.branchIds,
    statuses: typeof _filters?.statuses === 'string' ? _filters?.statuses?.split?.(',') : _filters?.statuses,
    importStatuses:
      typeof _filters?.importStatuses === 'string' ? _filters?.importStatuses?.split?.(',') : _filters?.importStatuses,
    testRecord: _filters?.testRecord === 'true' || _filters?.testRecord === true,
  };
}

function FamilyApplicationsQueue() {
  const { get: getUrlParams, update: updateUrlParams } = useUrlParams();
  const urlParamsSnapshot = getUrlParams();

  const [query, setQuery] = useState(urlParamsSnapshot?.query);
  const [quickSearchForm] = Form.useForm();
  const searchQuery = Form.useWatch('query', quickSearchForm);

  // Set initial filters
  const [filteredInfo, setFilteredInfo] = useState(() => {
    if (urlParamsSnapshot?.filters) {
      return processFiltersQueryParam(urlParamsSnapshot?.filters);
    }
    return {};
  });

  const [sorterInfo, setSorterInfo] = useState(
    urlParamsSnapshot?.sort
      ? {
          columnKey: urlParamsSnapshot?.sort?.[0],
          field: urlParamsSnapshot?.sort?.[1],
          order: urlParamsSnapshot?.sort?.[2],
        }
      : INITIAL_SORTER_INFO,
  );

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: urlParamsSnapshot?.current !== undefined ? urlParamsSnapshot?.current : 1,
    pageSize: urlParamsSnapshot?.pageSize !== undefined ? urlParamsSnapshot?.pageSize : PAGE_SIZE,
    showSizeChanger: true,
  });

  const getFormattedSortFields = (sorterField) => {
    if (sorterField) {
      const isNestedValues = Array.isArray(sorterField);
      return isNestedValues ? sorterField.reduce((total, current) => `${total}.${current}`) : sorterField;
    }
    return null;
  };

  const dashboardFilters = useMemo(() => {
    const filters = {};
    if (filteredInfo?.statuses?.length) filters.statuses = filteredInfo.statuses;
    if (filteredInfo?.branchIds?.length) filters.branchIds = filteredInfo.branchIds;
    if (filteredInfo?.importStatuses?.length) filters.importStatuses = filteredInfo.importStatuses;
    if (filteredInfo?.testRecord) filters.testRecord = filteredInfo.testRecord;
    return filters;
  }, [filteredInfo]);

  const params = useMemo(() => {
    const _queryParams = getUrlParams();
    const _searchQuery = _queryParams?.query;

    const formattedSortFields = getFormattedSortFields(sorterInfo.field);
    return {
      size: pagination?.pageSize,
      page: (pagination?.current || 1) - 1,
      ...(_searchQuery ? { query: _searchQuery } : dashboardFilters),
      sort: formattedSortFields
        ? `${formattedSortFields},${sorterInfo.order === 'ascend' ? 'asc' : 'desc'}`
        : undefined,
    };
  }, [dashboardFilters, pagination, sorterInfo, getUrlParams]);

  const fetchData = useCallback(
    (configs) => {
      const { params: configParams, body: configBody } = configs || {};

      setLoading(true);

      const _searchQuery = getUrlParams()?.query;
      const apiUrl = '/families/applications/search';

      commonService
        .post(apiUrl, configBody, { query: _searchQuery, ...configParams })
        .then((res) => {
          setData(res.content);
          setPagination((_prev) => ({ ..._prev, total: res.totalElements }));
        })
        .catch((error) => {
          console.log('Error fetching family applications', error);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [getUrlParams],
  );

  const onChange = useCallback(
    (pagination, filters, sorter) => {
      setSorterInfo(sorter);
      setPagination((_prev) => ({
        ..._prev,
        current: pagination.current,
        page: pagination.current - 1,
        pageSize: pagination.pageSize,
        size: pagination.pageSize,
      }));

      // Update query params
      updateUrlParams({
        current: pagination?.current,
        pageSize: pagination?.pageSize,
        sort: sorter?.columnKey === undefined ? null : `${sorter?.columnKey},${sorter?.field},${sorter?.order}`,
      });

      const _newFetchParams = {
        ...pagination,
        page: pagination.current - 1,
      };

      if (sorter?.columnKey) {
        if (sorter.field && !_newFetchParams.sort) {
          const fieldSorter = getFormattedSortFields(sorter.field);
          _newFetchParams.sort = `${fieldSorter},${sorter.order === 'ascend' ? 'asc' : 'desc'}`;
        }
      } else {
        _newFetchParams.sort = undefined;
      }

      fetchData({
        params: { ...params, ..._newFetchParams, size: _newFetchParams.pageSize || params.size },
        body: { ...filteredInfo },
      });
    },
    [fetchData, filteredInfo, params, updateUrlParams],
  );

  const state = useMemo(() => {
    return {
      data,
      loading,
      onChange,
      pagination,
    };
  }, [data, loading, onChange, pagination]);

  const onSearch = useCallback(
    (_query) => {
      setQuery(_query);

      // Clear all filters and sorting
      const _newFilters = {};
      setFilteredInfo(_newFilters);
      setSorterInfo({});

      // Update URL query params
      updateUrlParams({
        filters: _newFilters,
        query: _query,
      });

      fetchData({
        params: {
          ...params,
          current: 1,
          query: !!_query ? _query : undefined,
        },
        body: _newFilters,
      });
    },
    [fetchData, params, updateUrlParams],
  );

  const filtersAreApplied = Object.values(filteredInfo).some((d) => d !== null && d?.length > 0);
  const sortingIsApplied = sorterInfo?.order !== undefined;
  const filterOrSortingApplied = filtersAreApplied || sortingIsApplied;

  const ClearFiltersButton = useMemo(() => {
    return ({ ...rest }) => (
      <Button
        type="primary"
        ghost
        onClick={() => {
          const _newFilters = {
            branchIds: undefined,
            statuses: undefined,
            importStatuses: undefined,
            testRecord: undefined,
          };

          setFilteredInfo(_newFilters);

          // Clear URL params
          updateUrlParams({
            filters: null,
          });

          // Fetch new data
          setPagination((_prev) => ({ ..._prev, page: 0, current: 1 }));
          fetchData({
            params: {
              ...params,
              page: 0,
            },
            body: { ...filteredInfo, ..._newFilters },
          });
        }}
        {...rest}
      >
        Clear filters
      </Button>
    );
  }, [fetchData, filteredInfo, params, updateUrlParams]);

  useEffect(() => {
    fetchData({
      params: {
        ...params,
      },
      body: {
        ...filteredInfo,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mt-2 min-h-min flex space-x-4 flex-row items-start">
      <div className="w-full">
        <Form onFinish={({ query }) => onSearch(query.trim?.())} initialValues={{ query }} form={quickSearchForm}>
          <Card
            className="w-full mt-6"
            noBodyPadding
            title={
              <Space className="flex-col md:flex-row items-start md:items-center my-4 md:my-0" vCentered={false}>
                <div>
                  {state.loading !== true ? (
                    <>
                      <span>{BigNumber(state.pagination?.total || 0).toFormat()}</span> family application
                      {state.pagination?.total !== 1 ? 's' : ''}
                    </>
                  ) : (
                    <div></div>
                  )}
                </div>

                <Spacer />

                <Form.Item name="query" className="mb-0">
                  <Input.Search
                    size="large"
                    className="w-[400px]"
                    placeholder="Sponsor name, app or family ID"
                    onSearch={() => quickSearchForm.submit()}
                    suffix={
                      <Button
                        onClick={() => {
                          quickSearchForm.setFieldsValue({ query: '' });
                          onSearch('');
                        }}
                        className="-mr-1"
                        style={{
                          display: !searchQuery ? 'none' : undefined,
                        }}
                        shape="circle"
                        icon={<Close />}
                        size="small"
                        type="text"
                        aria-label="Clear search"
                      />
                    }
                    enterButton={
                      <Button
                        type="primary"
                        aria-label="Search by sponsor name or ID"
                        data-testid="quick-search-button"
                      >
                        Search <Search />
                      </Button>
                    }
                    data-testid="quick-search-text"
                  />
                </Form.Item>
              </Space>
            }
          >
            <FamilyDashboardFilters
              fetchData={(_newFilters) => {
                setPagination((_prev) => ({ ..._prev, page: 0, current: 1 }));
                fetchData({
                  params: {
                    ...params,
                    page: 0,
                  },
                  body: { ...filteredInfo, ..._newFilters },
                });
              }}
              filters={filteredInfo}
              setFilters={setFilteredInfo}
              ClearFiltersButton={ClearFiltersButton}
            />

            {state.data && (
              <ConfigProvider
                renderEmpty={() => (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      state.loading ? null : filterOrSortingApplied || searchQuery ? (
                        <>
                          There are no results that match your search. Try clearing filters
                          <ClearFiltersButton className="mx-auto mt-8" />
                        </>
                      ) : (
                        'No data to show'
                      )
                    }
                  />
                )}
              >
                <FamilyDashboardTable state={state} sorterInfo={sorterInfo} rowKey="id" />
              </ConfigProvider>
            )}
          </Card>
        </Form>
      </div>
    </div>
  );
}

export default function FamilyApplicationsDashboard() {
  return (
    <>
      <PageMeta title="Family Applications" />
      <PageTitleBar title="Family application queue" />
      <FamilyApplicationsQueue />
    </>
  );
}
